import earcut from "earcut";
import type { Map } from "maplibre-gl";
import type {
  Feature,
  Position,
  Polygon,
  MultiPolygon,
} from "geojson";
import type { ShadowConfig, TileHeightMesh } from "./types";

type BuildingLayerRef = {
  source: string;
  sourceLayer: string;
};

type ShadowGeometry = {
  vertexData: Float32Array;
  vertexCount: number;
  featureCount: number;
  tileMeshes: TileHeightMesh[];
};
type SourceFeature = Feature & {
  id?: string | number;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function parseNumericHeight(raw: unknown): number | null {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw;
  }

  if (typeof raw === "string") {
    const parsed = Number.parseFloat(raw.replace("m", "").trim());
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
}

function resolveHeightMeters(feature: SourceFeature, config: ShadowConfig): number {
  const props = (feature.properties ?? {}) as Record<string, unknown>;

  // OpenMapTiles-based styles (including liberty variants) commonly provide
  // render_height in meters; using it aligns shadow length with the displayed extrusion.
  const renderHeight = parseNumericHeight(props.render_height);
  if (renderHeight !== null && renderHeight > 0) {
    return clamp(renderHeight, 1, config.maxBuildingHeightMeters);
  }

  const explicitHeight = parseNumericHeight(props.height);
  if (explicitHeight !== null && explicitHeight > 0) {
    return clamp(explicitHeight, 1, config.maxBuildingHeightMeters);
  }

  const levels = parseNumericHeight(props["building:levels"]);
  if (levels !== null && levels > 0) {
    return clamp(levels * 3, 1, config.maxBuildingHeightMeters);
  }

  const renderedLevels = parseNumericHeight(props.render_min_height);
  if (renderedLevels !== null && renderedLevels > 0) {
    return clamp(renderedLevels, 1, config.maxBuildingHeightMeters);
  }

  return config.defaultBuildingHeightMeters;
}

function collectBuildingLayers(map: Map): BuildingLayerRef[] {
  const layers = map.getStyle().layers ?? [];
  const refs = new Set<string>();
  const result: BuildingLayerRef[] = [];

  for (const layer of layers) {
    if (layer.type !== "fill-extrusion") {
      continue;
    }

    if (!layer.source || !("source-layer" in layer) || !layer["source-layer"]) {
      continue;
    }

    const sourceLayer = layer["source-layer"];
    const key = `${layer.source}|${sourceLayer}`;
    if (refs.has(key)) {
      continue;
    }

    refs.add(key);
    result.push({
      source: layer.source,
      sourceLayer,
    });
  }

  return result;
}

function flattenPolygonRings(rings: Position[][]): {
  flat: number[];
  holeIndices: number[];
  coords: Position[];
} {
  const flat: number[] = [];
  const holeIndices: number[] = [];
  const coords: Position[] = [];

  let pointOffset = 0;
  for (let ringIndex = 0; ringIndex < rings.length; ringIndex += 1) {
    const ring = rings[ringIndex] ?? [];
    if (ring.length < 3) {
      continue;
    }

    if (ringIndex > 0) {
      holeIndices.push(pointOffset);
    }

    for (const position of ring) {
      const lng = position[0];
      const lat = position[1];
      if (typeof lng !== "number" || typeof lat !== "number") {
        continue;
      }
      flat.push(lng, lat);
      coords.push(position);
      pointOffset += 1;
    }
  }

  return { flat, holeIndices, coords };
}

function pushPolygonTriangles(
  map: Map,
  rings: Position[][],
  heightMeters: number,
  scaleX: number,
  scaleY: number,
  output: number[],
): number {
  const { flat, holeIndices } = flattenPolygonRings(rings);
  if (flat.length < 6) {
    return 0;
  }

  const indices = earcut(flat, holeIndices, 2);
  if (indices.length === 0) {
    return 0;
  }

  let written = 0;
  for (const index of indices) {
    const lng = flat[index * 2];
    const lat = flat[index * 2 + 1];
    if (lng === undefined || lat === undefined) {
      continue;
    }

    const point = map.project([lng, lat]);
    output.push(point.x * scaleX, point.y * scaleY, heightMeters);
    written += 1;
  }

  return written;
}

function pushFeatureTriangles(
  map: Map,
  feature: SourceFeature,
  heightMeters: number,
  scaleX: number,
  scaleY: number,
  output: number[],
): number {
  const geometry = feature.geometry;
  if (!geometry) {
    return 0;
  }

  if (geometry.type === "Polygon") {
    return pushPolygonTriangles(map, (geometry as Polygon).coordinates, heightMeters, scaleX, scaleY, output);
  }

  if (geometry.type === "MultiPolygon") {
    let count = 0;
    for (const polygon of (geometry as MultiPolygon).coordinates) {
      count += pushPolygonTriangles(map, polygon, heightMeters, scaleX, scaleY, output);
    }
    return count;
  }

  return 0;
}

export function collectBuildingShadowGeometry(map: Map, config: ShadowConfig): ShadowGeometry {
  const layerRefs = collectBuildingLayers(map);
  const triangleVertexData: number[] = [];
  const canvas = map.getCanvas();
  const cssWidth = Math.max(1, canvas.clientWidth);
  const cssHeight = Math.max(1, canvas.clientHeight);
  const pixelScaleX = (canvas.width / cssWidth) * config.textureScale;
  const pixelScaleY = (canvas.height / cssHeight) * config.textureScale;

  let featureCount = 0;

  for (const layerRef of layerRefs) {
    const features = map.querySourceFeatures(layerRef.source, {
      sourceLayer: layerRef.sourceLayer,
      validate: false,
    });

    for (let featureIndex = 0; featureIndex < features.length; featureIndex += 1) {
      const feature = features[featureIndex];
      if (!feature) {
        continue;
      }
      // Keep all source features, including tile-clipped fragments that share an id.
      // Deduping by id drops parts of buildings and breaks shadow geometry at z16.

      const heightMeters = resolveHeightMeters(feature, config);
      const added = pushFeatureTriangles(map, feature, heightMeters, pixelScaleX, pixelScaleY, triangleVertexData);
      if (added > 0) {
        featureCount += 1;
      }
    }
  }

  const vertexData = new Float32Array(triangleVertexData);
  const vertexCount = Math.floor(vertexData.length / 3);

  return {
    vertexData,
    vertexCount,
    featureCount,
    tileMeshes: [
      {
        tileKey: "viewport",
        vertexCount,
        featureCount,
      },
    ],
  };
}
